import type { BookingIntent } from "@/types/booking-intent";
import { storeNow, storeToday } from "@/utils/storeTime";

export const extractLocalBookingIntent = (
    text: string,
    storeTimezone: string,
): BookingIntent | null => {
    const lowerText = text.toLowerCase().trim();

    // We will "consume" parts of this string to see what's left
    let remainingText = lowerText;

    // Helper to consume a match
    const consume = (match: RegExpMatchArray | null) => {
        if (match && match[0]) {
            // Replace the matched text with spaces in the remainingText
            // We use replace with the exact match string.
            // Note: simplistic approach, assumes first occurrence if duplicates.
            // For better safety, we can use the index from match input if available?
            // Since we match on 'lowerText', and 'remainingText' starts as 'lowerText',
            // we can use string replacement. To be safe against duplicates, we might want to be careful,
            // but usually these specific matches are unique enough in short queries.
            remainingText = remainingText.replace(match[0], " ");
            return true;
        }
        return false;
    };

    // Basic required fields
    let date: string | null = null;
    let time: string | null = null;
    let adults: number | null = null;
    let kids = 0;
    let highchairs = 0;

    // 1. Detect Terms for "Today" and "Tomorrow" — anchored to the store's timezone, not the
    // guest's browser, so "tomorrow" always means the store's next calendar day.
    if (remainingText.includes("today") || remainingText.includes("tonight")) {
        date = storeToday(storeTimezone);
        remainingText = remainingText.replace("today", " ").replace("tonight", " ");
    } else if (remainingText.includes("tomorrow")) {
        date = storeNow(storeTimezone).add(1, "day").format("YYYY-MM-DD");
        remainingText = remainingText.replace("tomorrow", " ");
    }

    // 2. Detect Pax (Adults)
    // Patterns: "2 people", "table for 2", "2 pax", "2 adults", "table for 2 people"
    // We update the regex to optionally consume the unit after "table for X"
    const paxMatch = remainingText.match(
        /(\d+)\s*(people|pax|adults|guests)|table for\s*(\d+)(?:\s*(people|pax|adults|guests))?/,
    );
    if (paxMatch) {
        // Match structure:
        // 1: Count (Pattern A)
        // 2: Unit (Pattern A)
        // 3: Count (Pattern B - table for)
        // 4: Unit (Pattern B)

        const num = paxMatch[1] || paxMatch[3];
        if (num) adults = parseInt(num, 10);
        consume(paxMatch);
    }

    // 2.1 Detect Highchairs (check BEFORE kids to avoid conflicts with "baby"/"toddler")
    // Patterns: "2 highchairs", "1 high chair", "baby chair", "booster seat", "baby seat"
    const highchairMatch = remainingText.match(
        /(\d+)\s*(high\s*chairs?|baby\s*chairs?|booster\s*seats?|baby\s*seats?|toddler\s*chairs?)|a\s+(high\s*chair|baby\s*chair|booster\s*seat|baby\s*seat|toddler\s*chair)/,
    );
    if (highchairMatch) {
        const num = highchairMatch[1];
        // "a [chair type]" = 1, otherwise parse the number
        highchairs = num ? parseInt(num, 10) : 1;
        consume(highchairMatch);
    }

    // 2.2 Detect Kids
    // Patterns: "3 kids", "3 children", "1 child"
    // Also support: "boys", "girls", "toddlers", "babies"
    const kidsMatch = remainingText.match(
        /(\d+)\s*(kids?|children|child|boys?|girls?|toddlers?|bab(y|ies))/,
    );
    if (kidsMatch) {
        const num = kidsMatch[1];
        if (num) kids = parseInt(num, 10);
        consume(kidsMatch);
    }

    // 3. Detect Time
    // We want to avoid capturing "2" from "2 people" as a time.
    // So we require one of:
    // - "at" prefix (e.g. "at 7")
    // - "am"/"pm" suffix (e.g. "7pm", "7 pm")
    // - Colon usage (e.g. "7:00", "19:00")

    const timeMatch = remainingText.match(
        /(?:at\s+)(\d{1,2})(?::(\d{2}))?(?:\s*(am|pm))?|(\d{1,2})(?::(\d{2}))?\s*(am|pm)|(\d{1,2}):(\d{2})/,
    );

    if (timeMatch) {
        consume(timeMatch);

        const hourStr = timeMatch[1] || timeMatch[4] || timeMatch[7];
        const minStr = timeMatch[2] || timeMatch[5] || timeMatch[8];
        const meridian = timeMatch[3] || timeMatch[6];

        if (hourStr) {
            let hour = parseInt(hourStr, 10);
            const minute = minStr ? parseInt(minStr, 10) : 0;

            if (hour >= 0 && hour <= 24 && minute >= 0 && minute < 60) {
                // 12-hour conversion
                if (meridian === "pm" && hour < 12) hour += 12;
                if (meridian === "am" && hour === 12) hour = 0;

                // Heuristic: If "at [1-7]" without am/pm, likely PM?
                if (!meridian && hour > 0 && hour <= 7) {
                    hour += 12;
                }

                time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
            }
        }
    }

    // 4. Safety Check: Verify Remaining Text
    // Remove known "noise" words
    const NOISE_WORDS = [
        "at",
        "for",
        "please",
        "table",
        "book",
        "reservation",
        "want",
        "need",
        "a",
        "an",
        "the",
        "and",
        "in",
        "on",
        "my",
        "i",
        "to",
        "with",
    ];

    // Split remaining text into words, filter out empty and noise
    const leftOverWords = remainingText
        .split(/[\s,.]+/) // Split by whitespace, commas, dots
        .filter((w) => w.length > 0)
        .filter((w) => !NOISE_WORDS.includes(w));

    // If there are significant words left, it means we missed something important.
    // e.g. "window", "terrace", "vegan", "birthday", "nephews"
    // Heuristic: If any word > 0 length remains (after noise filter), invalid local parse.
    // (You might want to allow very short words or single chars, but safe bet is stricter)
    if (leftOverWords.length > 0) {
        // console.log("Unsafe local extraction. Leftovers:", leftOverWords);
        return null;
    }

    // If we successfully reasoned about Date + Time + Adults, we return a valid intent.
    if (date && time && adults) {
        return {
            date,
            time,
            adults,
            kids,
            highchairs,
            note: null,
            period: null,
            periodTitle: null,
        };
    }

    return null;
};
